import {
  Flex,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
  Skeleton,
} from '@chakra-ui/react';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { csvConversion, finleyAssert } from '@finley-technologies/common';
import { DbTable, Pipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import {
  useGetAssignedReceivablesForLoanProductLazyQueryGQLType,
  AssignmentConfirmationJobGQLType,
  useInitiateReceivableRepurchaseFromDataPipelineMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { useReceivableReconciliationAlertContext } from './ReceivableReconciliationAlertContext';
import { ReceivableReconciliationTable } from './ReceivableReconciliationTable';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useGetLatestMeshRunQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { usePollRepurchaseAndTransferJobs } from 'packages/loanProduct/hooks/usePollRepurchaseAndTransferJobs';
import { useUploadContext } from '~/packages/dataPipeline/hooks/UploadContext';

const parseReceivableErrorData = (
  assignmentErrors: Pipeline.PipelineError[],
) => {
  const externalReceivableIds: string[] = [];

  assignmentErrors.forEach((assignmentError) => {
    const {
      content: { tableData },
    } = assignmentError;
    const receivableErrorData = tableData
      ? csvConversion.csvStringToJsonArray({
          csvString: tableData.csvString,
        }).data
      : null;

    finleyAssert(
      receivableErrorData,
      'Expected to find receivable error data for errored master loan tape instance',
    );

    receivableErrorData.forEach((receivableError) => {
      externalReceivableIds.push(receivableError['Attempted Receivable ID']);
    });
  });

  return externalReceivableIds;
};

export const ReceivableReconciliation = ({
  implementationId,
  loanProductSlug,
  creditFacilitySlug,
  setAreAllReceivablesReconciled,
  kickedOffNewPipeline,
  closeDrawer,
}: {
  implementationId: string;
  loanProductSlug: string;
  creditFacilitySlug: string;
  setAreAllReceivablesReconciled: React.Dispatch<React.SetStateAction<boolean>>;
  kickedOffNewPipeline: boolean;
  closeDrawer: () => void;
}) => {
  const navigate = useNavigate();

  const {
    showErrorAlert,
    showInProgressAlert,
    showSuccessAlert,
  } = useReceivableReconciliationAlertContext();

  const {
    data: latestMeshRunData,
    loading: latestMeshRunLoading,
    startPolling,
    stopPolling,
  } = useGetLatestMeshRunQuery({
    fetchPolicy: 'no-cache',
    variables: {
      implementationId,
    },
  });

  useEffect(() => {
    startPolling(5000);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const { completeRecord, instances } = useUploadContext();

  const [externalReceivableIds, setExternalReceivableIds] = useState<
    string[]
  >();

  const [
    getReceivablesData,
    {
      data: receivablesData,
      loading: areReceivablesLoading,
      error: getReceivablesError,
    },
  ] = useGetAssignedReceivablesForLoanProductLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.assignedReceivablesForLoanProduct.length === 0) {
        setAreAllReceivablesReconciled(true);
      }
    },
  });

  const refetchReceivables = useCallback(() => {
    if (externalReceivableIds) {
      getReceivablesData({
        variables: { loanProductSlug, externalReceivableIds },
      });
    }
  }, [externalReceivableIds, getReceivablesData, loanProductSlug]);

  useEffect(() => {
    if (!completeRecord || latestMeshRunLoading || !latestMeshRunData) {
      return;
    }

    const latestMeshRunRecord = latestMeshRunData.getLatestMeshRun
      ? DbGraphUtils.deserializeMeshRunRecord(
          latestMeshRunData.getLatestMeshRun,
        )
      : null;

    const {
      meshRunRecord: { status, meshRunId },
      meshDocument: { artifacts },
    } = completeRecord;

    // must be the latest mesh run
    if (latestMeshRunRecord && latestMeshRunRecord.meshRunId !== meshRunId) {
      if (kickedOffNewPipeline) {
        navigate(`/data-pipeline/details/${latestMeshRunRecord.meshRunId}`);
        closeDrawer();
      }
      return;
    }

    const masterLoanTapeArtifact = artifacts.find(
      (artifact) => artifact.type === 'masterLoanTape',
    );

    // receivable reconciliation only shows up when master loan tape has errored
    if (status !== 'error' || !masterLoanTapeArtifact) {
      return;
    }

    const erroredMasterLoanTapeInstance = instances.find(
      (instance) =>
        instance.artifactId === masterLoanTapeArtifact.id &&
        instance.status === 'error',
    );

    if (!erroredMasterLoanTapeInstance) {
      return;
    }

    // TODO fix this type assertion
    const {
      statusDetails: { errors },
    } = (erroredMasterLoanTapeInstance as unknown) as DbTable.ArtifactInstance.ErrorRecord;

    // check if instance failed due to receivable assignment error
    const assignmentErrors = errors.filter((error) =>
      error.content.externalMessage.startsWith('Receivables already assigned'),
    );

    if (assignmentErrors.length > 0) {
      setExternalReceivableIds(parseReceivableErrorData(assignmentErrors));
    }
  }, [
    closeDrawer,
    completeRecord,
    instances,
    kickedOffNewPipeline,
    latestMeshRunData,
    latestMeshRunLoading,
    navigate,
  ]);

  useEffect(() => {
    if (externalReceivableIds) {
      getReceivablesData({
        variables: { loanProductSlug, externalReceivableIds },
      });
    }
  }, [externalReceivableIds, getReceivablesData, loanProductSlug]);

  const { repurchaseAndTransferJobs } = usePollRepurchaseAndTransferJobs({
    implementationId,
  });

  const [
    inProgressJob,
    setInProgressJob,
  ] = useState<AssignmentConfirmationJobGQLType | null>(null);

  // initiatedJobInfo is used to display alert when a job is first initiated, before jobs are polled next
  const [initiatedJobInfo, setInitiatedJobInfo] = useState<{
    id: string;
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
    receivableCount: number;
  } | null>(null);

  const [
    terminalJob,
    setTerminalJob,
  ] = useState<AssignmentConfirmationJobGQLType | null>(null);

  const [
    receivableCountForConfirmationJob,
    setReceivableCountForConfirmationJob,
  ] = useState<number | null>(null);

  useEffect(() => {
    if (initiatedJobInfo) {
      setTerminalJob(null);
      setReceivableCountForConfirmationJob(initiatedJobInfo.receivableCount);
    }
  }, [initiatedJobInfo]);

  useEffect(() => {
    const inProgressJobs = repurchaseAndTransferJobs.filter(
      (job) => job.status === 'inProgress',
    );
    finleyAssert(
      inProgressJobs.length <= 1,
      `Expected maximum one repurchase or transfer confirmation job to be in progress for [implementationId ${implementationId}]`,
    );
    let terminatedJob: AssignmentConfirmationJobGQLType | null = null;
    if (inProgressJobs.length === 1) {
      // a job is in progress
      setInProgressJob(inProgressJobs[0]);
      setInitiatedJobInfo(null);
      setTerminalJob(null);
    } else if (inProgressJob) {
      // a job was in progress and should now be in terminal status
      const jobId = inProgressJob.id;
      const jobRecord = repurchaseAndTransferJobs.find(
        (job) => job.id === jobId,
      );
      finleyAssert(jobRecord, `Expected to find job with id ${jobId}`);
      terminatedJob = jobRecord;
    } else if (initiatedJobInfo) {
      // job was kicked off in UI, but either we polled and the job record doesn't exist yet
      // or the job completed in between polls
      const jobId = initiatedJobInfo.id;
      const jobRecord = repurchaseAndTransferJobs.find(
        (job) => job.id === jobId,
      );
      terminatedJob = jobRecord ?? null;
    }
    if (terminatedJob) {
      setTerminalJob(terminatedJob);
      setInProgressJob(null);
      setInitiatedJobInfo(null);
      if (terminatedJob.status === 'success') {
        refetchReceivables();
      }
    }
  }, [
    implementationId,
    inProgressJob,
    initiatedJobInfo,
    refetchReceivables,
    repurchaseAndTransferJobs,
  ]);

  const [
    initiateReceivableRepurchaseFromDataPipeline,
    { error: initiateReceivableRepurchaseError },
  ] = useInitiateReceivableRepurchaseFromDataPipelineMutation({
    onCompleted: (data) => {
      setInitiatedJobInfo({
        id:
          data.initiateReceivableRepurchaseFromDataPipeline
            .repurchaseConfirmationJobId,
        jobType: 'repurchase',
        receivableCount:
          data.initiateReceivableRepurchaseFromDataPipeline
            .receivableRepurchaseCount,
      });
    },
    onError: () => {
      setInProgressJob(null);
    },
  });

  const initiateRepurchaseConfirmationJob = ({
    rowSelectionData,
  }: {
    rowSelectionData: any;
  }) => {
    // set dummy initial job info to display alert
    // then set actual initiated job info after it's kicked off
    setInitiatedJobInfo({
      id: 'job-id',
      jobType: 'repurchase',
      receivableCount: 0,
    });

    initiateReceivableRepurchaseFromDataPipeline({
      variables: {
        loanProductSlug,
        implementationId,
        externalReceivableIds: Object.keys(rowSelectionData),
      },
    });
  };

  useEffect(() => {
    if (
      !initiateReceivableRepurchaseError &&
      (initiatedJobInfo || inProgressJob)
    ) {
      const jobType = initiatedJobInfo?.jobType || inProgressJob?.jobType;
      if (jobType) {
        showInProgressAlert(jobType);
      }
    }
  }, [
    initiatedJobInfo,
    inProgressJob,
    showInProgressAlert,
    initiateReceivableRepurchaseError,
  ]);

  const allReceivablesReconciled = !!(
    receivablesData &&
    receivablesData.assignedReceivablesForLoanProduct.length === 0
  );

  useEffect(() => {
    if (
      !initiatedJobInfo &&
      !inProgressJob &&
      terminalJob &&
      terminalJob.status === 'success' &&
      receivableCountForConfirmationJob !== null
    ) {
      showSuccessAlert(
        terminalJob.jobType,
        receivableCountForConfirmationJob,
        allReceivablesReconciled,
      );
    }
  }, [
    initiatedJobInfo,
    inProgressJob,
    terminalJob,
    receivableCountForConfirmationJob,
    allReceivablesReconciled,
    showSuccessAlert,
  ]);

  useEffect(() => {
    if (initiateReceivableRepurchaseError && initiatedJobInfo) {
      showErrorAlert(initiatedJobInfo.jobType);
      refetchReceivables();
    } else if (
      !initiatedJobInfo &&
      !inProgressJob &&
      terminalJob &&
      terminalJob.status === 'error'
    ) {
      showErrorAlert(terminalJob.jobType);
      refetchReceivables();
    }
  }, [
    initiatedJobInfo,
    inProgressJob,
    terminalJob,
    receivableCountForConfirmationJob,
    allReceivablesReconciled,
    initiateReceivableRepurchaseError,
    refetchReceivables,
    showErrorAlert,
  ]);

  if (
    !externalReceivableIds ||
    !latestMeshRunData ||
    latestMeshRunLoading ||
    kickedOffNewPipeline
  ) {
    return null;
  }

  return (
    <Flex direction="column">
      {(areReceivablesLoading ||
        getReceivablesError ||
        !allReceivablesReconciled) && (
        <>
          <Typography variant="base-muted" TextProps={{ mt: '24px' }}>
            The Receivable IDs are already assigned to a facility that differs
            from the one specified on the loan tape. Select the receivables you
            would like to repurchase onto the balance sheet.
          </Typography>
          <Box mt="24px">
            <>
              {areReceivablesLoading && <Skeleton mb="24px" height="50vh" />}
              {!areReceivablesLoading &&
                receivablesData &&
                !getReceivablesError && (
                  <ReceivableReconciliationTable
                    receivablesData={receivablesData}
                    creditFacilitySlug={creditFacilitySlug}
                    initiateRepurchaseConfirmationJob={
                      initiateRepurchaseConfirmationJob
                    }
                    jobInProgress={
                      !!inProgressJob ||
                      (!initiateReceivableRepurchaseError && !!initiatedJobInfo)
                    }
                  />
                )}

              {getReceivablesError && (
                <Alert status="error" mb="32px">
                  <Flex gap={1}>
                    <AlertIcon />
                    <AlertDescription>
                      An unexpected error occurred when fetching invalid
                      receivables for loan tape. Please contact Finley for
                      support.
                    </AlertDescription>
                  </Flex>
                </Alert>
              )}
            </>
          </Box>
        </>
      )}
    </Flex>
  );
};
